import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Clock, Users, TrendingUp } from 'lucide-react';

const getCurrentSlot = () => {
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const h = ist.getHours();
  if (h >= 6 && h < 9) return 'Q1';
  if (h >= 9 && h < 12) return 'Q2';
  if (h >= 12 && h < 15) return 'Q3';
  if (h >= 15 && h < 18) return 'Q4';
  if (h >= 18 && h < 22) return 'Q5';
  return null;
};

const isPastSlot = (slot) => {
  const order = ['Q1', 'Q2', 'Q3', 'Q4', 'Q5'];
  const currentSlot = getCurrentSlot();
  if (!currentSlot) return true;
  return order.indexOf(slot) < order.indexOf(currentSlot);
};

const SLOT_ORDER = ['Q1', 'Q2', 'Q3', 'Q4', 'Q5'];

const formatCurrency = (v) => {
  if (v == null) return '--';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

export default function ClusterCard({ cluster }) {
  const [expanded, setExpanded] = useState(false);

  const currentSlot = useMemo(() => getCurrentSlot(), []);

  const {
    tl_name = 'Unknown',
    cluster_target = 0,
    revenue_done = 0,
    staff_present = 0,
    staff_total = 0,
    gyms = [],
    time_slots = {},
  } = cluster || {};

  const pct = cluster_target > 0 ? Math.round((revenue_done / cluster_target) * 100) : 0;

  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const barTrack = pct >= 80 ? 'bg-emerald-100' : pct >= 50 ? 'bg-amber-100' : 'bg-red-100';
  const badgeColor = pct >= 80
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : pct >= 50
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-red-50 text-red-700 ring-red-200';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header row - always visible */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col items-start min-w-0">
            <span className="text-sm font-semibold text-slate-900 truncate">
              {tl_name}
            </span>
            <span className="text-xs text-slate-500">
              {(cluster?.gym_count || gyms.length)} gyms
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Revenue vs target */}
          <div className="text-right hidden sm:block">
            <div className="text-sm font-semibold text-slate-900">
              <TrendingUp className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
              {formatCurrency(revenue_done)} / {formatCurrency(cluster_target)}
            </div>
          </div>

          {/* Completion badge */}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ring-1 ${badgeColor}`}>
            {pct}%
          </span>

          {/* Staff count */}
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Users className="w-3.5 h-3.5" />
            <span>{staff_present}/{staff_total}</span>
          </div>

          {/* Expand toggle */}
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />
          }
        </div>
      </button>

      {/* Progress bar */}
      <div className={`mx-4 mb-3 h-2 rounded-full overflow-hidden ${barTrack}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      {/* Expanded section */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-3">
          {/* Time slot mini-cards */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Time Slots
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {SLOT_ORDER.map((slot) => {
                const s = time_slots[slot] || {};
                const isCurrent = slot === currentSlot;
                const isPast = isPastSlot(slot);
                const isFuture = !isCurrent && !isPast;
                const slotPct = s.target > 0 ? Math.round((s.done / s.target) * 100) : 0;
                const met = slotPct >= 100;

                let cardClasses = 'rounded-lg p-2 text-center border transition-all ';
                if (isCurrent) {
                  cardClasses += 'border-blue-400 bg-blue-50 ring-2 ring-blue-300 animate-pulse';
                } else if (isPast) {
                  cardClasses += met
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-red-200 bg-red-50';
                } else {
                  cardClasses += 'border-slate-200 bg-slate-50 opacity-60';
                }

                return (
                  <div key={slot} className={cardClasses}>
                    <div className="text-[10px] font-medium text-slate-500 mb-0.5">
                      {slot} <span className="hidden sm:inline">({s.label || ''})</span>
                    </div>
                    {isFuture ? (
                      <div className="text-xs text-slate-400 font-semibold">
                        {formatCurrency(s.target || 0)}
                      </div>
                    ) : (
                      <>
                        <div className="text-xs font-bold">
                          {isPast && !isCurrent && (
                            <span className={met ? 'text-emerald-600' : 'text-red-600'}>
                              {met ? '\u2713' : '\u2717'}{' '}
                            </span>
                          )}
                          <span className={isCurrent ? 'text-blue-700' : 'text-slate-800'}>
                            {formatCurrency(s.done || 0)}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400">
                          / {formatCurrency(s.target || 0)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Gym drilldown table */}
          {gyms.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Gym Breakdown
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-100">
                      <th className="text-left py-1.5 pr-2 font-medium">Gym</th>
                      <th className="text-right py-1.5 px-2 font-medium">Target</th>
                      <th className="text-right py-1.5 px-2 font-medium">Done</th>
                      <th className="text-right py-1.5 px-2 font-medium">Outstanding</th>
                      <th className="text-right py-1.5 px-2 font-medium">Staff</th>
                      <th className="text-center py-1.5 pl-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gyms.map((gym) => {
                      const gymPct = gym.target > 0
                        ? Math.round((gym.revenue_done / gym.target) * 100)
                        : 0;
                      const outstanding = Math.max(0, (gym.target || 0) - (gym.revenue_done || 0));
                      const dotColor = gymPct >= 80
                        ? 'bg-emerald-500'
                        : gymPct >= 50
                          ? 'bg-amber-500'
                          : 'bg-red-500';

                      return (
                        <tr key={gym.gym_id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-1.5 pr-2 text-slate-700 font-medium truncate max-w-[120px]">
                            {gym.gym_name}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-500">
                            {formatCurrency(gym.target)}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-900 font-semibold">
                            {formatCurrency(gym.revenue_done)}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-500">
                            {formatCurrency(outstanding)}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-500">
                            {gym.staff_present}/{gym.staff_total}
                          </td>
                          <td className="py-1.5 pl-2 text-center">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
